import { useEffect, useRef, useState } from 'react';
import { PaneSwitch, type Pane } from '@/components/pane-switch';
import { useAuth } from '@clerk/clerk-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  api,
  type CommercialInvoice,
  type CommitCommercialInvoiceResponse,
  type GenerateCommercialInvoiceResponse,
  type ProformaInvoice,
  type Shipment,
} from '@/lib/api';
import { useModalDismiss } from '@/lib/use-modal-dismiss';
import { ShipFromPicker, useShipFromLocations } from '@/components/ship-from-locations';
import { isElevated, useMyRole } from '@/lib/use-my-role';

interface Props {
  open: boolean;
  onClose: () => void;
  proforma: ProformaInvoice;
  /** Null: issue before the shipment is booked (a 3PL ships with our CI). */
  shipment: Shipment | null;
  /** The CI this one replaces, so a reissue starts from what was issued. */
  previous?: CommercialInvoice | null;
  rfqId?: string;
}

const CURRENCY_SYMBOL: Record<string, string> = {
  USD: '$', EUR: '€', GBP: '£', CAD: 'CA$', AUD: 'A$',
  JPY: '¥', CHF: 'CHF ', AED: 'AED ', SGD: 'S$',
};

function fmtMoney(cents: number, currency: string): string {
  const sym = CURRENCY_SYMBOL[currency] ?? `${currency} `;
  if (currency === 'JPY') return `${sym}${Math.round(cents / 100).toLocaleString('en-US')}`;
  return `${sym}${(cents / 100).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

export function CommercialInvoiceModal({ open, onClose, proforma, shipment, previous = null, rfqId }: Props) {
  const dismiss = useModalDismiss(onClose);
  const { getToken } = useAuth();
  const qc = useQueryClient();
  const shipFromQuery = useShipFromLocations();
  // Issuing is owner/admin only (a CI is a legal customs document); anyone who
  // can write may preview. Unknown role (still loading) keeps the button.
  const role = useMyRole();
  const canIssue = role === null || isElevated(role);

  // A reissue starts from the CI it replaces; a first issue starts blank and
  // lets the server fill HTS / ECCN / origin from the vendor quote. ECCN is
  // NOT pre-filled with EAR99: that overrode the vendor's own classification.
  const initial = () => ({
    htsCode: previous?.htsCode ?? '',
    eccn: previous?.eccn ?? '',
    countryOfOrigin: previous?.countryOfOrigin ?? '',
    portOfExport: previous?.portOfExport ?? '',
    portOfEntry: previous?.portOfEntry ?? '',
    endUse: previous?.endUse ?? '',
    endUser: previous?.endUser ?? '',
    licenseCitation: previous?.licenseCitation ?? 'NLR',
    soldToAddress: previous?.soldToAddress ?? '',
    shipFromId: previous?.shipFromLocationId ?? '',
    grossWeightKg: previous?.grossWeightKg ? String(parseFloat(previous.grossWeightKg)) : '',
    netWeightKg: previous?.netWeightKg ? String(parseFloat(previous.netWeightKg)) : '',
    packageCount: previous?.packageCount != null ? String(previous.packageCount) : '',
  });
  const [f, setF] = useState(initial);
  // The previous CI's ship-from location, when it has since been removed in
  // Billing & settings: the form falls back to the vendor's origin and says so,
  // instead of sending an id the server rejects as unknown.
  const [removedShipFrom, setRemovedShipFrom] = useState<string | null>(null);
  const set = <K extends keyof ReturnType<typeof initial>>(k: K, v: string) => setF((cur) => ({ ...cur, [k]: v }));
  const { htsCode, eccn, countryOfOrigin, portOfExport, portOfEntry, endUse, endUser, licenseCitation, soldToAddress } = f;

  const [preview, setPreview] = useState<GenerateCommercialInvoiceResponse | null>(null);
  const [saved, setSaved] = useState<CommercialInvoice | null>(null);
  const iframeRef = useRef<HTMLIFrameElement | null>(null);

  useEffect(() => {
    if (open) {
      const next = initial();
      // A first CI preselects the default ship-from location, if one is set.
      if (!previous && !next.shipFromId) {
        next.shipFromId = shipFromQuery.data?.locations.find((l) => l.isDefault)?.id ?? '';
      }
      const locations = shipFromQuery.data?.locations;
      if (next.shipFromId && locations && !locations.some((l) => l.id === next.shipFromId)) {
        setRemovedShipFrom(previous?.shipFromLabel ?? 'The previous location');
        next.shipFromId = '';
      } else {
        setRemovedShipFrom(null);
      }
      setF(next);
    } else {
      setPreview(null);
      setSaved(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, previous?.id, shipFromQuery.data]);

  function invalidateLists() {
    qc.invalidateQueries({ queryKey: ['commercial-invoices'] });
    qc.invalidateQueries({ queryKey: ['shipments'] });
    if (rfqId) qc.invalidateQueries({ queryKey: ['rfq', rfqId] });
    qc.invalidateQueries({ queryKey: ['rfqs'] });
  }

  function buildBody() {
    const body: Record<string, unknown> = {
      proformaInvoicePublicId: proforma.id,
    };
    if (shipment) body.shipmentPublicId = shipment.id;
    if (f.shipFromId) body.shipFromLocationId = f.shipFromId;
    const num = (v: string) => { const n = Number(v); return v.trim() && Number.isFinite(n) && n > 0 ? n : undefined; };
    if (num(f.grossWeightKg)) body.grossWeightKg = num(f.grossWeightKg);
    if (num(f.netWeightKg)) body.netWeightKg = num(f.netWeightKg);
    if (num(f.packageCount) && Number.isInteger(num(f.packageCount))) body.packageCount = num(f.packageCount);
    if (htsCode) body.htsCode = htsCode;
    if (eccn) body.eccn = eccn;
    if (countryOfOrigin) body.countryOfOrigin = countryOfOrigin;
    if (portOfExport) body.portOfExport = portOfExport;
    if (portOfEntry) body.portOfEntry = portOfEntry;
    if (endUse) body.endUse = endUse;
    if (endUser) body.endUser = endUser;
    if (licenseCitation) body.licenseCitation = licenseCitation;
    if (soldToAddress) body.soldToAddress = soldToAddress;
    return body;
  }

  const previewMutation = useMutation({
    mutationFn: async () => {
      return api<GenerateCommercialInvoiceResponse>('/api/commercial-invoices/generate', {
        method: 'POST',
        tokenGetter: () => getToken(),
        body: JSON.stringify(buildBody()),
      });
    },
    onSuccess: (data) => {
      setPreview(data);
      setSaved(null);
      // Show the ECCN the document will state, so issuing is a reviewed choice.
      if (!f.eccn && data.eccn) set('eccn', data.eccn);
    },
  });

  const saveMutation = useMutation({
    mutationFn: async () => {
      return api<CommitCommercialInvoiceResponse>('/api/commercial-invoices', {
        method: 'POST',
        tokenGetter: () => getToken(),
        idempotencyKey: crypto.randomUUID(),
        body: JSON.stringify(buildBody()),
      });
    },
    onSuccess: (data) => {
      setSaved(data.commercialInvoice);
      if (data.commercialInvoice.html) {
        setPreview({
          ciRef: data.commercialInvoice.ref,
          proformaRef: data.commercialInvoice.proformaRef,
          quoteRef: data.commercialInvoice.quoteRef,
          rfqRef: data.commercialInvoice.rfqRef,
          shipmentRef: data.commercialInvoice.shipmentRef,
          generatedAt: data.commercialInvoice.generatedAt,
          currency: data.commercialInvoice.currency,
          qty: data.commercialInvoice.qty,
          unitPriceCents: data.commercialInvoice.unitPriceCents,
          totalCents: data.commercialInvoice.totalCents,
          html: data.commercialInvoice.html,
        });
      }
      invalidateLists();
    },
  });

  useEffect(() => {
    if (!preview?.html || !iframeRef.current) return;
    const doc = iframeRef.current.contentDocument;
    if (!doc) return;
    doc.open();
    doc.write(preview.html);
    doc.close();
  }, [preview]);

  const downloadHtml = () => {
    if (!preview) return;
    const blob = new Blob([preview.html], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${preview.ciRef}.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const copyHtml = async () => {
    if (!preview) return;
    try {
      await navigator.clipboard.writeText(preview.html);
    } catch {
      window.prompt('Copy the HTML manually:', preview.html.slice(0, 100) + '…');
    }
  };

  const openInNewTab = () => {
    if (!preview) return;
    const win = window.open('', '_blank');
    if (!win) return;
    win.document.open();
    win.document.write(preview.html);
    win.document.close();
  };

  // Phones show one pane at a time (see pane-switch.tsx). The preview takes over as soon as a
  // document exists or is regenerated, and every open starts on the right pane.
  const [pane, setPane] = useState<Pane>('edit');
  const previewReady = !!preview?.html;
  useEffect(() => {
    setPane(previewReady ? 'preview' : 'edit');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, preview]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4"
      {...dismiss}
    >
      <div className="bg-background border border-border rounded-lg shadow-xl w-full max-w-5xl max-h-[94dvh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <div>
            <h2 className="font-semibold">Issue commercial invoice</h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              Customs paperwork that ships with the goods. Pulls compliance fields from the vendor quote + shipment;
              broker can override below.
            </p>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground text-sm" aria-label="Close">✕</button>
        </div>

        <PaneSwitch pane={pane} onChange={setPane} previewReady={previewReady} editLabel={'Edit'} />
        <div className="flex-1 overflow-y-auto lg:overflow-hidden grid grid-cols-1 lg:grid-cols-[340px_1fr]">
          <aside className={`border-b lg:border-r border-border lg:overflow-auto ${pane === 'edit' ? '' : 'hidden lg:block'} p-5 space-y-4 bg-muted/20`}>
            <div>
              <div className="text-xs uppercase tracking-wide text-muted-foreground mb-2">Source</div>
              <div className="text-sm font-mono">Proforma {proforma.ref}</div>
              <div className="text-sm font-mono">{shipment ? `Shipment ${shipment.ref}` : 'Before shipment'}</div>
              <div className="mt-2 font-mono text-sm font-semibold text-primary">
                {fmtMoney(proforma.totalCents, proforma.currency)}
                {proforma.qty > 1 && (
                  <span className="text-muted-foreground font-normal"> ({proforma.qty} ea)</span>
                )}
              </div>
            </div>

            {proforma.status !== 'paid' && (
              <div className="rounded border border-amber-300 bg-amber-50 px-2.5 py-2 text-xs text-amber-900">
                The proforma is <strong>{proforma.status}</strong>, not paid. The invoice will state the payment terms
                instead of &ldquo;Paid in full&rdquo;.
              </div>
            )}

            {!saved && (
              <div className="space-y-3">
                <label className="block">
                  <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">Ships from</div>
                  <ShipFromPicker
                    value={f.shipFromId}
                    noneLabel="The vendor's origin"
                    className="w-full px-2 py-1.5 text-sm bg-background border border-border rounded"
                    onPick={(l) => { set('shipFromId', l?.id ?? ''); setRemovedShipFrom(null); }}
                  />
                  {removedShipFrom && (
                    <div className="mt-1 text-[11px] text-amber-700">
                      {removedShipFrom} was removed from your saved locations, so this invoice will show the vendor&rsquo;s origin. Pick another location if the goods ship from one.
                    </div>
                  )}
                </label>
                {!shipment && (
                  <div className="grid grid-cols-3 gap-2">
                    <label className="block">
                      <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">Gross kg</div>
                      <input inputMode="decimal" value={f.grossWeightKg} onChange={(e) => set('grossWeightKg', e.target.value)}
                        className="w-full px-2 py-1.5 text-sm bg-background border border-border rounded font-mono" />
                    </label>
                    <label className="block">
                      <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">Net kg</div>
                      <input inputMode="decimal" value={f.netWeightKg} onChange={(e) => set('netWeightKg', e.target.value)}
                        className="w-full px-2 py-1.5 text-sm bg-background border border-border rounded font-mono" />
                    </label>
                    <label className="block">
                      <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">Boxes</div>
                      <input inputMode="numeric" value={f.packageCount} onChange={(e) => set('packageCount', e.target.value)}
                        className="w-full px-2 py-1.5 text-sm bg-background border border-border rounded font-mono" />
                    </label>
                  </div>
                )}
                <label className="block">
                  <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">Sold-to / Buyer address</div>
                  <textarea
                    value={soldToAddress}
                    onChange={(e) => set('soldToAddress', e.target.value)}
                    placeholder={'Full buyer postal address\ne.g. Thales SA\nRue des Frères Taymans 28\n1480 Tubize, Belgium'}
                    rows={3}
                    maxLength={500}
                    className="w-full px-2 py-1.5 text-sm bg-background border border-border rounded"
                  />
                </label>
                <label className="block">
                  <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">HTS code</div>
                  <input
                    value={htsCode}
                    onChange={(e) => set('htsCode', e.target.value)}
                    placeholder="auto from vendor quote"
                    maxLength={40}
                    className="w-full px-2 py-1.5 text-sm bg-background border border-border rounded font-mono"
                  />
                </label>
                <label className="block">
                  <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">ECCN</div>
                  <input
                    value={eccn}
                    onChange={(e) => set('eccn', e.target.value)}
                    placeholder="from vendor quote — else e.g. EAR99"
                    maxLength={40}
                    className="w-full px-2 py-1.5 text-sm bg-background border border-border rounded font-mono"
                  />
                </label>
                <label className="block">
                  <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">Country of origin</div>
                  <input
                    value={countryOfOrigin}
                    onChange={(e) => set('countryOfOrigin', e.target.value)}
                    placeholder="US, FR, GB, ..."
                    maxLength={80}
                    className="w-full px-2 py-1.5 text-sm bg-background border border-border rounded font-mono"
                  />
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <label className="block">
                    <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">Port of export</div>
                    <input
                      value={portOfExport}
                      onChange={(e) => set('portOfExport', e.target.value)}
                      placeholder="JFK"
                      maxLength={120}
                      className="w-full px-2 py-1.5 text-sm bg-background border border-border rounded font-mono"
                    />
                  </label>
                  <label className="block">
                    <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">Port of entry</div>
                    <input
                      value={portOfEntry}
                      onChange={(e) => set('portOfEntry', e.target.value)}
                      placeholder="LHR"
                      maxLength={120}
                      className="w-full px-2 py-1.5 text-sm bg-background border border-border rounded font-mono"
                    />
                  </label>
                </div>
                <label className="block">
                  <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">License citation</div>
                  <input
                    value={licenseCitation}
                    onChange={(e) => set('licenseCitation', e.target.value)}
                    placeholder="NLR / EAR99 / 9A991"
                    maxLength={40}
                    className="w-full px-2 py-1.5 text-sm bg-background border border-border rounded font-mono"
                  />
                </label>
                <label className="block">
                  <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">End-user</div>
                  <input
                    value={endUser}
                    onChange={(e) => set('endUser', e.target.value)}
                    placeholder="Ultimate consignee"
                    maxLength={200}
                    className="w-full px-2 py-1.5 text-sm bg-background border border-border rounded"
                  />
                </label>
                <label className="block">
                  <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">End-use</div>
                  <textarea
                    value={endUse}
                    onChange={(e) => set('endUse', e.target.value)}
                    placeholder="What the part is being used for"
                    rows={2}
                    maxLength={500}
                    className="w-full px-2 py-1.5 text-sm bg-background border border-border rounded"
                  />
                </label>
              </div>
            )}

            {!preview ? (
              <button
                onClick={() => previewMutation.mutate()}
                disabled={previewMutation.isPending}
                className="w-full px-3 py-2 text-sm font-medium bg-primary text-primary-foreground rounded-md disabled:opacity-50"
              >
                {previewMutation.isPending ? 'Generating…' : 'Generate preview'}
              </button>
            ) : !saved ? (
              <div className="space-y-2">
                <div className="border border-border rounded p-3 bg-background">
                  <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">Preview</div>
                  <div className="text-sm font-mono">{preview.ciRef}</div>
                  <div className="text-[11px] text-muted-foreground mt-1.5 italic">Not issued yet.</div>
                </div>
                {!eccn.trim() && (
                  <div className="text-[11px] text-destructive">
                    Enter the ECCN to issue — the vendor quote has none. Use EAR99 only if the part is not on the Commerce Control List.
                  </div>
                )}
                {canIssue ? (
                  <button
                    onClick={() => saveMutation.mutate()}
                    disabled={saveMutation.isPending || !eccn.trim()}
                    className="w-full px-3 py-2 text-sm font-medium bg-primary text-primary-foreground rounded-md disabled:opacity-50"
                  >
                    {saveMutation.isPending ? 'Issuing…' : 'Issue commercial invoice'}
                  </button>
                ) : (
                  <div className="rounded border border-border bg-muted/40 px-2.5 py-2 text-xs text-muted-foreground">
                    Only an owner or admin can issue a commercial invoice. Download or open the preview and send it to them.
                  </div>
                )}
                <button onClick={downloadHtml} className="w-full px-3 py-2 text-sm border border-border rounded-md hover:bg-muted">Download preview .html</button>
                <button onClick={copyHtml} className="w-full px-3 py-2 text-sm border border-border rounded-md hover:bg-muted">Copy preview HTML</button>
                <button onClick={openInNewTab} className="w-full px-3 py-2 text-sm border border-border rounded-md hover:bg-muted">Open in new tab</button>
                <button
                  onClick={() => setPreview(null)}
                  className="w-full px-3 py-2 text-xs text-muted-foreground hover:text-foreground"
                >
                  ← Adjust &amp; regenerate
                </button>
              </div>
            ) : (
              <div className="space-y-2">
                <div className="border border-border rounded p-3 bg-background">
                  <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">
                    Issued · <span className="font-semibold text-emerald-600">{saved.status}</span>
                  </div>
                  <div className="text-sm font-mono">{saved.ref}</div>
                  {saved.issuedAt && (
                    <div className="text-[11px] text-muted-foreground mt-1">{new Date(saved.issuedAt).toLocaleString()}</div>
                  )}
                </div>
                <div className="border-t border-border pt-2 mt-2 space-y-2">
                  <button onClick={downloadHtml} className="w-full px-3 py-2 text-sm border border-border rounded-md hover:bg-muted">Download .html</button>
                  <button onClick={copyHtml} className="w-full px-3 py-2 text-sm border border-border rounded-md hover:bg-muted">Copy HTML</button>
                  <button onClick={openInNewTab} className="w-full px-3 py-2 text-sm border border-border rounded-md hover:bg-muted">Open in new tab</button>
                </div>
              </div>
            )}

            {previewMutation.isError && (
              <div className="text-sm text-destructive">{(previewMutation.error as { message?: string })?.message ?? 'Generation failed'}</div>
            )}
            {saveMutation.isError && (
              <div className="text-sm text-destructive">{(saveMutation.error as { message?: string })?.message ?? 'Issue failed'}</div>
            )}
          </aside>

          <section className={`overflow-hidden bg-[#f4f6fa] items-stretch min-h-[55dvh] lg:min-h-0 ${pane === 'preview' ? 'flex' : 'hidden lg:flex'}`}>
            {preview ? (
              <iframe ref={iframeRef} title="Commercial invoice preview" className="w-full h-full border-0 bg-white" />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-sm text-muted-foreground">
                Set fields on the left and click <span className="mx-1 font-medium">Generate preview</span>.
              </div>
            )}
          </section>
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-border">
          <button onClick={onClose} className="px-3 py-1.5 text-sm rounded-md text-muted-foreground hover:bg-muted">Done</button>
        </div>
      </div>
    </div>
  );
}
